<template>
  <div id="app">
    <a class="skip-link" href="#site-main">跳到主要内容</a>
    <el-container class="site-shell">
      <el-header
        v-if="!isAdminRoute"
        class="site-header"
        :class="{ 'site-header--home': $route.path === '/', 'is-scrolled': isScrolled }"
      >
        <div class="site-header__inner">
          <router-link class="logo" to="/" aria-label="返回网站首页">
            <img :src="displayLogo" :alt="siteConfig.siteName" fetchpriority="high" />
          </router-link>
          <button
            class="mobile-menu-toggle"
            :class="{ 'is-open': mobileMenuOpen }"
            type="button"
            :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
            :aria-label="mobileMenuOpen ? '关闭导航菜单' : '打开导航菜单'"
            @click="toggleMobileMenu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <el-menu
            :default-active="defaultActive"
            class="desktop-nav"
            mode="horizontal"
            @select="handleSelect"
            :router="router"
          >
            <el-menu-item
              v-for="item in visibleNavItems"
              :key="item.path"
              :index="item.path"
            >{{ item.name }}</el-menu-item>
          </el-menu>
        </div>
      </el-header>

      <div
        v-if="!isAdminRoute"
        class="mobile-nav-overlay"
        :class="{ 'is-open': mobileMenuOpen }"
        :aria-hidden="mobileMenuOpen ? 'false' : 'true'"
        @click="closeMobileMenu"
      ></div>
      <aside
        v-if="!isAdminRoute"
        class="mobile-nav-drawer"
        :class="{ 'is-open': mobileMenuOpen }"
        :aria-hidden="mobileMenuOpen ? 'false' : 'true'"
        aria-label="网站导航"
      >
        <div class="mobile-nav-drawer__head">
          <div>
            <strong>网站导航</strong>
            <small>{{ siteConfig.siteName }}</small>
          </div>
          <button type="button" aria-label="关闭导航菜单" @click="closeMobileMenu">×</button>
        </div>
        <nav class="mobile-nav-drawer__links">
          <router-link
            v-for="(item, index) in visibleNavItems"
            :key="item.path"
            :to="item.path"
            :class="{ 'is-active': defaultActive === item.path }"
            @click.native="closeMobileMenu"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            {{ item.name }}
          </router-link>
        </nav>
        <p class="mobile-nav-drawer__foot">数字化工程管理 · 专业技术服务</p>
      </aside>

      <el-main id="site-main" :class="{ 'site-main--admin': isAdminRoute }" tabindex="-1">
        <transition name="page-fade" mode="out-in">
          <router-view />
        </transition>
      </el-main>

      <footer class="footer" v-show="isShow">
        <div class="footer-content">
          <div class="footer-brand">
            <img class="footer-brand__logo" :src="displayLogo" :alt="siteConfig.siteName" />
            <p>以专业技术与可靠服务，持续推动企业数字化建设。</p>
          </div>
          <ul class="content-nav">
            <li v-for="group in siteConfig.footerGroups" :key="group.title">
              <p>{{ group.title }}</p>
              <template v-for="(item, itemIndex) in group.items">
                <router-link
                  v-if="getFooterRoute(item)"
                  :key="`${group.title}-${item}-${itemIndex}`"
                  class="footer-link"
                  :to="getFooterRoute(item)"
                >{{ item }}</router-link>
                <span v-else :key="`${group.title}-${item}-${itemIndex}`">{{ item }}</span>
              </template>
            </li>
          </ul>
          <div class="footer-qr">
            <img :src="defaultQrCode" alt="关注尚亚科技二维码" />
            <span>扫码了解更多</span>
          </div>
        </div>
        <div class="copyright">
          <span>© {{ currentYear }} {{ siteConfig.siteName || '尚亚科技' }} 版权所有</span>
        </div>
      </footer>

      <transition name="back-top-fade">
        <button
          v-show="showBackToTop && isShow && !isAdminRoute"
          class="back-to-top"
          type="button"
          aria-label="返回页面顶部"
          @click="scrollToTop"
        >
          <i class="el-icon-arrow-up"></i>
        </button>
      </transition>
    </el-container>
  </div>
</template>

<script>
import defaultLogo from "./assets/img/shangya-logo.png";
import defaultQrCode from "./assets/img/ercode.png";
import { getDefaultSiteConfig, loadSiteConfig } from "./services/siteConfig";

const PRODUCT_DETAIL_PATHS = ["/traffic-boost", "/ganghaolai-mall"];
const FOOTER_ROUTE_MAP = {
  "流量速推": "/traffic-boost",
  "刚好来商城": "/ganghaolai-mall"
};

export default {
  data() {
    return {
      router: true,
      defaultActive: "/",
      isShow: false,
      defaultLogo,
      displayLogo: defaultLogo,
      logoLoadRequestId: 0,
      pendingLogoImage: null,
      isDisposed: false,
      defaultQrCode,
      siteConfig: getDefaultSiteConfig(),
      mobileMenuOpen: false,
      showBackToTop: false,
      isScrolled: false,
      currentYear: new Date().getFullYear()
    };
  },
  computed: {
    visibleNavItems() {
      return this.siteConfig.navItems.filter(item => item.visible !== false);
    },
    isAdminRoute() {
      return this.$route.path.startsWith("/admin");
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        this.defaultActive = PRODUCT_DETAIL_PATHS.includes(route.path) ? "/product" : route.path;
        this.updateFooterVisible(route.path);
        this.closeMobileMenu();
        this.$nextTick(() => window.scrollTo(0, 0));
      }
    }
  },
  created() {
    loadSiteConfig()
      .then(config => {
        if (this.isDisposed) return;
        this.siteConfig = config;
        this.preloadConfiguredLogo(config.logoUrl);
      })
      .catch(() => {
        if (this.isDisposed) return;
        this.siteConfig = getDefaultSiteConfig();
        this.preloadConfiguredLogo("");
      });
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    this.isDisposed = true;
    this.logoLoadRequestId += 1;
    this.clearPendingLogoImage();
    window.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("keydown", this.handleKeydown);
    if (typeof document !== "undefined") {
      document.body.classList.remove("mobile-nav-open");
    }
  },
  methods: {
    clearPendingLogoImage() {
      if (!this.pendingLogoImage) return;
      this.pendingLogoImage.onload = null;
      this.pendingLogoImage.onerror = null;
      this.pendingLogoImage = null;
    },
    preloadConfiguredLogo(logoUrl) {
      if (this.isDisposed) return;
      const normalizedLogo = typeof logoUrl === "string" ? logoUrl.trim() : "";
      const requestId = ++this.logoLoadRequestId;
      this.clearPendingLogoImage();
      if (!normalizedLogo || typeof Image === "undefined") {
        this.displayLogo = defaultLogo;
        return;
      }

      const image = new Image();
      this.pendingLogoImage = image;
      image.onload = () => {
        if (!this.isDisposed && requestId === this.logoLoadRequestId) {
          this.pendingLogoImage = null;
          image.onload = null;
          image.onerror = null;
          this.displayLogo = normalizedLogo;
        }
      };
      image.onerror = () => {
        if (!this.isDisposed && requestId === this.logoLoadRequestId) {
          this.pendingLogoImage = null;
          image.onload = null;
          image.onerror = null;
          this.displayLogo = defaultLogo;
        }
      };
      image.src = normalizedLogo;
    },
    getFooterRoute(item) {
      return Object.prototype.hasOwnProperty.call(FOOTER_ROUTE_MAP, item)
        ? FOOTER_ROUTE_MAP[item]
        : "";
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      this.syncMobileMenuLock();
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      this.syncMobileMenuLock();
    },
    syncMobileMenuLock() {
      if (typeof document !== "undefined") {
        document.body.classList.toggle("mobile-nav-open", this.mobileMenuOpen);
      }
    },
    handleKeydown(event) {
      if (event.key === "Escape" && this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    },
    handleScroll() {
      const offset = window.pageYOffset;
      this.showBackToTop = offset > 520;
      this.isScrolled = offset > 12;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    handleSelect(key) {
      this.defaultActive = key;
      this.updateFooterVisible(key);
    },
    updateFooterVisible(path) {
      this.isShow = path !== "/" && path !== "/login" && path !== "/investment" && !path.startsWith("/admin");
    }
  }
};
</script>

<style lang="scss">
* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

html,
body,
#app {
  min-height: 100%;
}

#app {
  color: var(--ink-800);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.site-shell {
  min-height: 100vh;
  flex-direction: column;
}

.skip-link {
  position: fixed;
  top: 10px;
  left: 14px;
  z-index: 3000;
  padding: 10px 16px;
  border-radius: 10px;
  color: #fff;
  background: var(--brand-800);
  text-decoration: none;
  box-shadow: var(--shadow-sm);
  transform: translateY(-160%);
  transition: transform 0.2s ease;
}

.skip-link:focus {
  transform: translateY(0);
}

.site-header.el-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  height: 72px !important;
  padding: 0;
  border-bottom: 1px solid rgba(220, 232, 239, 0.82);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 0 0 rgba(7, 43, 66, 0);
  backdrop-filter: blur(18px);
  transition: background var(--duration-base) ease, box-shadow var(--duration-base) ease,
    border-color var(--duration-base) ease;
}

/* 滚动后抬升：阴影加深、底色收紧，制造与内容的层级分离 */
.site-header.el-header.is-scrolled {
  border-bottom-color: rgba(220, 232, 239, 0.5);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 10px 30px rgba(7, 43, 66, 0.1);
}

.site-header__inner {
  display: flex;
  width: min(calc(100% - 40px), var(--content-width));
  height: 100%;
  margin: 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 36px;
}

.site-header .logo {
  display: flex;
  width: min(280px, 28vw);
  height: 100%;
  padding: 13px 0;
  align-items: center;
  flex: 0 1 280px;
}

.site-header .logo img {
  display: block;
  width: 100%;
  max-height: 46px;
  object-fit: contain;
  object-position: left center;
}

.desktop-nav.el-menu--horizontal {
  height: 72px;
  border-bottom: 0;
  background: transparent;
}

.desktop-nav.el-menu--horizontal > .el-menu-item {
  position: relative;
  height: 72px;
  padding: 0 19px;
  border-bottom: 0 !important;
  color: var(--ink-600);
  line-height: 72px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.04em;
}

.desktop-nav.el-menu--horizontal > .el-menu-item::after {
  content: "";
  position: absolute;
  right: 19px;
  bottom: 12px;
  left: 19px;
  height: 3px;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--brand-600), var(--accent-500));
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.3s var(--ease-out);
}

.desktop-nav.el-menu--horizontal > .el-menu-item:hover,
.desktop-nav.el-menu--horizontal > .el-menu-item.is-active {
  color: var(--brand-700);
  background: transparent;
}

.desktop-nav.el-menu--horizontal > .el-menu-item:hover::after,
.desktop-nav.el-menu--horizontal > .el-menu-item.is-active::after {
  transform: scaleX(1);
}

.el-main {
  width: 100%;
  padding: 0 !important;
  flex: 1;
}

.footer {
  width: 100%;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.8);
  background:
    radial-gradient(circle at 15% 20%, rgba(31, 145, 199, 0.24), transparent 36%),
    linear-gradient(135deg, var(--brand-950), var(--brand-800));
}

.footer-content {
  display: grid;
  width: min(calc(100% - 40px), var(--content-width));
  margin: 0 auto;
  padding: 54px 0 42px;
  grid-template-columns: minmax(220px, 1.2fr) minmax(420px, 2fr) 150px;
  align-items: start;
  gap: clamp(28px, 5vw, 72px);
}

.footer-brand__logo {
  width: min(250px, 100%);
  max-height: 46px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.94);
  object-fit: contain;
}

.footer-brand p {
  max-width: 310px;
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 14px;
  line-height: 1.9;
}

.content-nav {
  display: grid;
  list-style: none;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.content-nav li {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
}

.content-nav li p {
  position: relative;
  margin-bottom: 14px;
  padding-bottom: 12px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.content-nav li p::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 28px;
  height: 2px;
  background: var(--accent-500);
}

.content-nav li span,
.footer-link {
  padding: 4px 0;
  color: rgba(255, 255, 255, 0.62);
  font-size: 13px;
  line-height: 1.55;
}

.footer-link {
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: #fff;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.footer-link:focus {
  outline: 2px solid var(--accent-500);
  outline-offset: 3px;
  color: #fff;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.footer-qr {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
}

.footer-qr img {
  width: 124px;
  height: 124px;
  padding: 8px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.18);
}

.footer-qr span {
  font-size: 12px;
  letter-spacing: 0.12em;
}

.copyright {
  min-height: 44px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: var(--veil-soft);
  text-align: center;
}

.copyright span {
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  line-height: 44px;
  letter-spacing: 0.05em;
}

.mobile-menu-toggle,
.mobile-nav-overlay,
.mobile-nav-drawer {
  display: none;
}

.back-to-top {
  position: fixed;
  right: 24px;
  bottom: 28px;
  z-index: 900;
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(145deg, var(--brand-600), var(--brand-800));
  box-shadow: 0 12px 32px rgba(7, 66, 99, 0.28);
  cursor: pointer;
}

.back-top-fade-enter-active,
.back-top-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.back-top-fade-enter,
.back-top-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 1100px) {
  .desktop-nav.el-menu--horizontal > .el-menu-item {
    padding: 0 12px;
  }

  .desktop-nav.el-menu--horizontal > .el-menu-item::after {
    right: 12px;
    left: 12px;
  }

  .footer-content {
    grid-template-columns: 1fr 2fr;
  }

  .footer-qr {
    display: none;
  }
}

@media (max-width: 768px) {
  .site-header.el-header {
    height: var(--mobile-header-height) !important;
  }

  .site-header__inner {
    width: 100%;
    padding: 0 var(--mobile-gutter);
    padding-left: calc(var(--mobile-gutter) + env(safe-area-inset-left));
    padding-right: calc(var(--mobile-gutter) + env(safe-area-inset-right));
  }

  .site-header .logo {
    width: min(230px, calc(100% - 64px));
    height: var(--mobile-header-height);
    padding: 8px 0;
    flex-basis: 230px;
  }

  .site-header .logo img {
    height: 40px;
    max-height: 40px;
  }

  .desktop-nav {
    display: none !important;
  }

  .mobile-menu-toggle {
    display: flex;
    width: var(--mobile-touch-size);
    height: var(--mobile-touch-size);
    flex: 0 0 var(--mobile-touch-size);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border: 1px solid #dcebf3;
    border-radius: 12px;
    background: var(--brand-50);
    color: var(--brand-700);
    cursor: pointer;
  }

  .mobile-menu-toggle span {
    width: 21px;
    height: 2px;
    border-radius: 2px;
    background: currentColor;
    transition: transform 0.24s ease, opacity 0.2s ease;
  }

  .mobile-menu-toggle.is-open span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }

  .mobile-menu-toggle.is-open span:nth-child(2) {
    opacity: 0;
  }

  .mobile-menu-toggle.is-open span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }

  .mobile-nav-overlay {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1090;
    visibility: hidden;
    opacity: 0;
    background: rgba(4, 28, 46, 0.58);
    backdrop-filter: blur(3px);
    transition: opacity 0.25s ease, visibility 0.25s ease;
  }

  .mobile-nav-overlay.is-open {
    visibility: visible;
    opacity: 1;
  }

  .mobile-nav-drawer {
    display: flex;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1100;
    width: 86vw;
    max-width: 360px;
    padding: 12px 18px 22px;
    padding-top: calc(12px + env(safe-area-inset-top));
    padding-right: calc(18px + env(safe-area-inset-right));
    padding-bottom: calc(22px + env(safe-area-inset-bottom));
    flex-direction: column;
    background:
      radial-gradient(circle at 100% 0, rgba(31, 145, 199, 0.12), transparent 36%),
      #fff;
    box-shadow: -18px 0 48px rgba(8, 48, 76, 0.22);
    transform: translateX(105%);
    transition: transform 0.3s var(--ease-out);
  }

  .mobile-nav-drawer.is-open {
    transform: translateX(0);
  }

  .mobile-nav-drawer__head {
    display: flex;
    min-height: 68px;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--line);
  }

  .mobile-nav-drawer__head > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .mobile-nav-drawer__head strong {
    color: var(--brand-900);
    font-size: 18px;
  }

  .mobile-nav-drawer__head small {
    max-width: 220px;
    overflow: hidden;
    color: var(--ink-400);
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-nav-drawer__head button {
    width: var(--mobile-touch-size);
    height: var(--mobile-touch-size);
    border: 0;
    background: transparent;
    color: var(--brand-700);
    font-size: 30px;
    line-height: 1;
  }

  .mobile-nav-drawer__links {
    display: flex;
    padding-top: 16px;
    flex: 1;
    flex-direction: column;
    overflow-y: auto;
  }

  .mobile-nav-drawer__links a {
    display: flex;
    min-height: 54px;
    padding: 0 14px;
    align-items: center;
    gap: 13px;
    border-radius: 12px;
    color: var(--ink-800);
    font-size: 16px;
    text-decoration: none;
  }

  .mobile-nav-drawer__links a span {
    color: var(--ink-400);
    font-size: 10px;
    letter-spacing: 0.08em;
  }

  .mobile-nav-drawer__links a.is-active {
    color: var(--brand-700);
    background: linear-gradient(90deg, var(--brand-100), var(--brand-50));
    font-weight: 700;
  }

  .mobile-nav-drawer__links a.is-active span {
    color: var(--brand-500);
  }

  .mobile-nav-drawer__foot {
    padding-top: 18px;
    border-top: 1px solid var(--line);
    color: var(--ink-400);
    font-size: 11px;
    letter-spacing: 0.08em;
  }

  .footer-content {
    width: 100%;
    padding: 38px var(--mobile-gutter) 30px;
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .footer-brand__logo {
    width: 220px;
  }

  .footer-brand p {
    margin-top: 14px;
  }

  .content-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px 18px;
  }

  .footer-qr {
    display: none;
  }

  .copyright {
    padding-bottom: env(safe-area-inset-bottom);
  }

  .copyright span {
    line-height: 40px;
  }

  .back-to-top {
    right: 16px;
    bottom: calc(18px + env(safe-area-inset-bottom));
    width: 44px;
    height: 44px;
  }
}

@media (max-width: 420px) {
  .content-nav {
    grid-template-columns: 1fr;
  }
}
</style>
